import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../home/Navbar.css";
import "../home/mediaCss.css";
import logo from "../../Images/logo/isbm-university.webp";
import { Modal, Button, Accordion } from "react-bootstrap";
import { FiSearch } from "react-icons/fi";

const MenubarMob = () => {
  const [scrolled, setScrolled] = useState(false);
  const [schools, setSchools] = useState([]);
  const [coursesCache, setCoursesCache] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedProgramme, setExpandedProgramme] = useState(null);

  // Fetch Schools
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await axios.get(
          "https://isbmuniversity.edu.in/admin-portal/schoolsApi"
        );
        setSchools(response.data.data);
      } catch (error) {
        console.error("Error fetching schools:", error);
      }
    };
    fetchSchools();
  }, []);

  // Fetch courses for School of Pharmacy
  useEffect(() => {
    const fetchPharmacyCourses = async () => {
      try {
        const response = await axios.get(
          `https://isbmuniversity.edu.in/admin-portal/coursesList/school-of-pharmacy`
        );
        setCoursesCache(prev => ({
          ...prev,
          "school-of-pharmacy": response.data.data || []
        }));
      } catch (error) {
        console.error("Error fetching pharmacy courses:", error);
      }
    };
    fetchPharmacyCourses();
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Filter out "School of Health and Allied Science" and "School of Pharmacy"
  const filteredSchools = useMemo(() => {
    return schools.filter(
      (school) => 
        school.name !== "school-of-health-and-allied-science" && 
        school.name !== "school-of-pharmacy"
    );
  }, [schools]);

  // Create custom school of health entry
  const schoolOfHealthEntry = useMemo(() => ({
    id: 100,
    name: "school-of-health",
    title: "School of Health and Allied Science",
    description: "School of Health combines pharmacy and healthcare education",
    status: "Active"
  }), []);

  // Filter courses for Aeren Pharmacy (only DPharma)
  const getAerenPharmacyCourses = useMemo(() => {
    if (!coursesCache["school-of-pharmacy"]) return [];
    
    // Filter only courses with title containing "Diploma in Pharmacy" or "D Pharma"
    return coursesCache["school-of-pharmacy"].filter(course => 
      course.title.toLowerCase().includes("diploma in pharmacy") || 
      course.title.toLowerCase().includes("d pharma")
    );
  }, [coursesCache["school-of-pharmacy"]]);

  // Get all pharmacy courses
  const getPharmacyCourses = useMemo(() => {
    return coursesCache["school-of-pharmacy"] || [];
  }, [coursesCache["school-of-pharmacy"]]);

  // Dropdown Content
  const dropdownContent = {
    "About Us": [
      { label: "About ISBM University", link: "/about-us", target: "_blank" },
      { label: "Vision and Mission", link: "/vision-mission", target: "_blank" },
      { label: "Leadership and Management", link: "/leadership-and-management", target: "_blank" },
      { label: "University Administration", link: "/university-administration", target: "_blank" },
      { label: "Board of Advisors", link: "/board-advisor", target: "_blank" },
      { label: "Messages of the Dignitaries", link: "/dignitaries-messages", target: "_blank" },
      { label: "Statutes and Ordinances", link: "/statues-and-ordinance", target: "_blank" },
      { label: "Accreditations and Recognitions", link: "/accreditation-and-recognitions", target: "_blank" },
      { label: "Awards", link: "/awards", target: "_blank" },
      { label: "Media Coverage", link: "/media-coverage", target: "_blank" },
      { label: "Raj Bhavan", link: "https://rajbhavancg.gov.in/", target: "_blank" },
      { label: "Code of Conduct", link: "/code-of-conduct", target: "_blank" },
    ],
    Academics: [
      { label: "Academic Philosophy", link: "/academic-philosophy", target: "_blank" },
      { label: "Academic Partners", link: "/academic-partner", target: "_blank" },
      { label: "Online Resources", link: "/online-resources", target: "_blank" },
      { label: "Teaching Methodology / Pedagogy", link: "/teaching-methodology-pedagogy", target: "_blank" },
      { label: "MOOC's with ISBM University", link: "/mooc-with-isbm", target: "_blank" },
      { label: "MOU's and Collaborations", link: "/mous-and-collaborations", target: "_blank" },
      { label: "Blogs", link: "/blog", target: "_blank" },
      { label: "Academic Calendar", link: "/academic-calender", target: "_blank" },
    ],
    Programmes: [
      ...filteredSchools.map((school, index) => ({
        key: index,
        label: school.title,
        link: `/school-details/${String(school.name || "")
          .toLowerCase()
          .replace(/\s+/g, "-")}`,
        isHealthSchool: false
      })),
      {
        key: "school-of-health",
        label: schoolOfHealthEntry.title,
        link: `/school-of-health`,
        isHealthSchool: true
      }
    ].sort((a, b) => a.label.localeCompare(b.label)), // Sort alphabetically
    Admissions: [
      { label: "Programmes and Eligibilities", link: "/programmes-and-eligibilities", target: "_blank" },
      { label: "Programmes By Qualification", link: "/programmes-by-qualification", target: "_blank" },
      { label: "Skill Development", link: "/skill-development", target: "_blank" },
      { label: "Research", link: "/research", target: "_blank" },
      { label: "Apply Online", link: "/apply-online", target: "_blank" },
      { label: "Online Payment", link: "/pay-fees-online", target: "_blank" },
      { label: "Payment Procedure Guidelines", link: "/payment-procedure-guidelines", target: "_blank" },
      { label: "Admission Procedure Guidelines", link: "/admission-procedure-guidelines", target: "_blank" },
      { label: "Admission Helpdesk", link: "/admission-help-desk", target: "_blank" },
    ],
    "Student Services": [
      { label: "FAQ", link: "/faqs", target: "_blank" },
      { label: "Scholarship", link: "/scholarship", target: "_blank" },
      { label: "Anti-Ragging Policy", link: "/anti-ragging-policy", target: "_blank" },
      { label: "Download", link: "/download-form", target: "_blank" },
      { label: "Various Application", link: "/various-application", target: "_blank" },
      { label: "Alumni Portal", link: "https://isbmalumni.com/", target: "_blank" },
      { label: "Voter Portal", link: "https://voterportal.eci.gov.in/", target: "_blank" },
      { label: "Anti Ragging Committee", link: "/anti-ragging-committee", target: "_blank" },
      { label: "Anti-Ragging Squad", link: "/anti-ragging-squad", target: "_blank" },
      { label: "Scheduled Caste and Scheduled Tribe Committee", link: "/sc-and-st-committee", target: "_blank" },
      { label: "Other Backward Class Committee", link: "/obc-committee", target: "_blank" },
      { label: "Student's Grievance Redressal Committee", link: "/student-grievance-committee", target: "_blank" },
      { label: "Internal Complaints Committee", link: "/icc-committee", target: "_blank" },
      { label: "Cultural Committee", link: "/cultural-committee", target: "_blank" },
      { label: "Student Council", link: "/student-council", target: "_blank" },
      { label: "Student ERP", link: "https://isbmuniversity.online/", target: "_blank" },
      { label: "Student Grievance", link: "/student-grievance", target: "_blank" },
    ],
    Placements: [
      { label: "About Placements", link: "/about-placements", target: "_blank" },
      { label: "Career Counselling", link: "/career-counselling", target: "_blank" },
      { label: "Global Readiness", link: "/global-readiness", target: "_blank" },
      { label: "Recruiters", link: "/recruiters", target: "_blank" },
      { label: "Campus Recruitment and Interview Training", link: "/campus-recruitment-interview-training", target: "_blank" },
      { label: "Work at ISBM University", link: "/work-at-isbmu", target: "_blank" },
    ],
    Campus: [
      { label: "Gariyaband Campus", link: "/gariyaband-campus" },
      { label: "Raipur Campus", link: "/raipur-campus" },
      { label: "Library", link: "/library", target: "_blank" },
      { label: "Laboratory", link: "/laboratory", target: "_blank" },
      { label: "Sports Facilities", link: "/sports-facilities", target: "_blank" },
      { label: "Extra Curricular", link: "/extra-curricular", target: "_blank" },
      { label: "Digitized Learning System", link: "/digitized-learning-system", target: "_blank" },
      { label: "Bus Transportation", link: "/bus-transportation", target: "_blank" },
      { label: "Hostel", link: "/hostel", target: "_blank" },
    ],
    International: [
      { label: "International Partnerships", link: "/international-partnerships", target: "_blank" },
      { label: "International Students", link: "/international-students", target: "_blank" },
    ],
  };

  // Flatten search index
  const searchIndex = useMemo(() => {
    const base = Object.entries(dropdownContent).flatMap(([_, items]) =>
      items.map((i) => ({ label: i.label, link: i.link }))
    );
    
    // Add courses to search index
    if (coursesCache["school-of-pharmacy"]) {
      coursesCache["school-of-pharmacy"].forEach((c) => {
        base.push({
          label: `Course: ${c.title}`,
          link: `/course-details/${c.name}`,
        });
      });
    }
    
    return base;
  }, [dropdownContent, coursesCache]);

  const filteredSuggestions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter((item) => item.label.toLowerCase().includes(q))
      .slice(0, 10);
  }, [searchTerm, searchIndex]);

  const goSearch = (dest) => {
    if (dest) {
      window.location.href = dest;
    } else if (searchTerm.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchTerm.trim())}`;
    }
    setSearchOpen(false);
  };

  // Handle programme item click for School of Health
  const handleProgrammeItemClick = (item) => {
    if (item.isHealthSchool) {
      setExpandedProgramme(expandedProgramme === "school-of-health" ? null : "school-of-health");
    }
  };

  return (
    <>
      <nav
        className={`navbar navbar-expand-lg bg-body-tertiary custom-mb-navbar ${scrolled ? "fixed-top scrolled" : ""
          }`}
      >
        <div className="container-fluid p-0">
          <a className="navbar-brand ps-2" href="/">
            <img className="logo" src={logo} alt="University Logo" />
          </a>
          <Button
            variant="light"
            size="sm"
            className="d-md-none text-dark ms-5"
            onClick={() => setSearchOpen(true)}
            aria-label="Open search"
          >
            <FiSearch size={20} style={{ transform: "scaleY(1)" }} />
          </Button>
          <button
            className="navbar-toggler me-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse bg-blue" id="navbarNav">
            <ul className="navbar-nav p-4">
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  About Us
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["About Us"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Academics
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Academics"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Programmes
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Programmes"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      {subItem.isHealthSchool ? (
                        <div className="border-bottom">
                          <div 
                            className="dropdown-item d-flex justify-content-between align-items-center"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleProgrammeItemClick(subItem)}
                          >
                            <span>{subItem.label}</span>
                            <span>
                              {expandedProgramme === "school-of-health" ? "−" : "+"}
                            </span>
                          </div>
                          
                          {expandedProgramme === "school-of-health" && (
                            <div className="px-3 py-2">
                              {/* School of Pharmacy Section */}
                              <div className="mb-3">
                                <h6 className="fw-bold text-primary mb-2">School of Pharmacy</h6>
                                {getPharmacyCourses.length > 0 ? (
                                  <ul className="list-unstyled">
                                    {getPharmacyCourses.map((course, idx) => (
                                      <li key={idx} className="mb-2">
                                        <a
                                          className="text-dark d-block"
                                          href={`/course-details/${course.name}`}
                                          target="_blank"
                                        >
                                          <small>{course.title}</small>
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <small className="text-muted">Loading courses...</small>
                                )}
                              </div>
                              
                              {/* Aeren Pharmacy Section */}
                              <div>
                                <h6 className="fw-bold text-primary mb-2">Alpha Pharmacy</h6>
                                {getAerenPharmacyCourses.length > 0 ? (
                                  <ul className="list-unstyled">
                                    {getAerenPharmacyCourses.map((course, idx) => (
                                      <li key={idx} className="mb-2">
                                        <a
                                          className="text-dark d-block"
                                          href={`/course-details/${course.name}`}
                                          target="_blank"
                                        >
                                          <small>{course.title}</small>
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <small className="text-muted">Loading Alpha Pharmacy courses...</small>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          className="dropdown-item border-bottom"
                          href={subItem.link}
                          target="_blank"
                        >
                          {subItem.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Admissions
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Admissions"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Student Services
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Student Services"].map(
                    (subItem, subIndex) => (
                      <li key={subIndex}>
                        <a
                          className="dropdown-item border-bottom"
                          href={subItem.link}
                          target={subItem.target || "_self"}
                        >
                          {subItem.label}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Placements
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Placements"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Campus
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["Campus"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-golden fs-6"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  International
                </a>
                <ul
                  className="dropdown-menu"
                  style={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {dropdownContent["International"].map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        className="dropdown-item border-bottom"
                        href={subItem.link}
                        target={subItem.target || "_self"}
                      >
                        {subItem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item dropdown">
                <a className="nav-link text-golden fs-6"
                  href="https://isbmuniversity.online/"
                  target="_blank">
                  Student Login
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Search Modal */}
      <Modal
        show={searchOpen}
        onHide={() => setSearchOpen(false)}
        centered
        size="sm"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search ISBM University</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Search courses, schools, or pages…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") goSearch();
            }}
            autoFocus
          />

          {searchTerm.trim() && (
            <div className="mt-3">
              {filteredSuggestions.length ? (
                <div className="list-group">
                  {filteredSuggestions.map((s, idx) => (
                    <button
                      key={`${s.link}-${idx}`}
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => goSearch(s.link)}
                    >
                      <span>{s.label}</span>
                      <i className="ti ti-arrow-right" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-muted small">
                  No suggestions. Press Enter to search the site.
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSearchOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MenubarMob;